if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput = this.form.querySelector('input[name="id"]');
        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtons = Array.from(this.querySelectorAll('[type="submit"]'));
        this.submitButton = this.submitButtons[0];
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        const activeSubmitButton = evt.submitter || this.submitButton;
        const activeSubmitButtonText = activeSubmitButton?.querySelector('span');
        const activeSpinner = activeSubmitButton?.querySelector('.loading__spinner');
        const isBuyNow = activeSubmitButton?.dataset.buyNow === 'true';

        evt.preventDefault();
        if (!activeSubmitButton || activeSubmitButton.getAttribute('aria-disabled') === 'true') return;

        // Validate form inputs
        if (!this.validateForm()) {
          this.handleErrorMessage('Please check your input and try again.');
          return;
        }

        this.handleErrorMessage();

        this.submitButton = activeSubmitButton;
        this.submitButtonText = activeSubmitButtonText;

        activeSubmitButton.setAttribute('aria-disabled', true);
        activeSubmitButton.classList.add('loading');
        activeSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // Re-resolve cart container in case it was not available during constructor time.
        if (!this.cart) {
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        }

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = activeSubmitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              activeSubmitButton.setAttribute('aria-disabled', true);
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (isBuyNow) {
              window.location = window.routes?.checkout_url || '/checkout';
              return;
            } else if (!this.cart) {
              this.showCartFallbackPopup(response);
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            activeSubmitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) activeSubmitButton.removeAttribute('aria-disabled');
            activeSpinner?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      showCartFallbackPopup(response) {
        this.ensureCartFallbackPopup();
        if (!this.cartFallbackPopup) return;

        const titleNode = this.cartFallbackPopup.querySelector('[data-cart-fallback-title]');
        const messageNode = this.cartFallbackPopup.querySelector('[data-cart-fallback-message]');
        const checkoutButton = this.cartFallbackPopup.querySelector('[data-cart-fallback-checkout]');
        const cartButton = this.cartFallbackPopup.querySelector('[data-cart-fallback-cart]');

        if (titleNode) {
          titleNode.textContent = window.variantStrings?.addToCart || 'Added to cart';
        }

        if (messageNode) {
          const itemTitle = response?.title || '';
          messageNode.textContent = itemTitle
            ? `${itemTitle} has been added to your cart.`
            : 'Item has been added to your cart.';
        }

        if (checkoutButton) {
          checkoutButton.href = window.routes?.checkout_url || '/checkout';
        }

        if (cartButton) {
          cartButton.href = window.routes?.cart_url || '/cart';
        }

        this.cartFallbackPopup.hidden = false;
        requestAnimationFrame(() => this.cartFallbackPopup.classList.add('is-visible'));
      }

      hideCartFallbackPopup() {
        if (!this.cartFallbackPopup) return;
        this.cartFallbackPopup.classList.remove('is-visible');
        setTimeout(() => {
          if (this.cartFallbackPopup && !this.cartFallbackPopup.classList.contains('is-visible')) {
            this.cartFallbackPopup.hidden = true;
          }
        }, 200);
      }

      ensureCartFallbackPopup() {
        if (this.cartFallbackPopup) return;

        const existingPopup = document.getElementById('cart-fallback-popup');
        if (existingPopup) {
          this.cartFallbackPopup = existingPopup;
          return;
        }

        if (!document.getElementById('cart-fallback-popup-style')) {
          const style = document.createElement('style');
          style.id = 'cart-fallback-popup-style';
          style.textContent = `
            .cart-fallback-popup {
              position: fixed;
              inset: 0;
              z-index: 9999;
              display: grid;
              place-items: center;
              opacity: 0;
              pointer-events: none;
              transition: opacity 0.2s ease;
            }
            .cart-fallback-popup.is-visible {
              opacity: 1;
              pointer-events: auto;
            }
            .cart-fallback-popup__overlay {
              position: absolute;
              inset: 0;
              background: rgba(0, 0, 0, 0.45);
            }
            .cart-fallback-popup__dialog {
              position: relative;
              width: min(92vw, 44rem);
              background: #fff;
              border-radius: 10px;
              padding: 2rem;
              box-shadow: 0 1.2rem 4rem rgba(0, 0, 0, 0.2);
            }
            .cart-fallback-popup__title {
              margin: 0 0 0.8rem;
              font-size: 2rem;
              line-height: 1.3;
            }
            .cart-fallback-popup__message {
              margin: 0 0 1.6rem;
              font-size: 1.4rem;
              line-height: 1.5;
              color: #4b4b4b;
            }
            .cart-fallback-popup__actions {
              display: grid;
              grid-template-columns: 1fr;
              gap: 0.8rem;
            }
            @media (min-width: 750px) {
              .cart-fallback-popup__actions {
                grid-template-columns: repeat(3, minmax(0, 1fr));
              }
            }
            .cart-fallback-popup__button {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-height: 4rem;
              border-radius: 6px;
              border: 1px solid #222;
              background: #fff;
              color: #222;
              text-decoration: none;
              font-size: 1.4rem;
              padding: 0 1.2rem;
            }
            .cart-fallback-popup__button--primary {
              background: #222;
              color: #fff;
            }
          `;
          document.head.appendChild(style);
        }

        const popup = document.createElement('div');
        popup.id = 'cart-fallback-popup';
        popup.className = 'cart-fallback-popup';
        popup.hidden = true;
        popup.innerHTML = `
          <div class="cart-fallback-popup__overlay" data-cart-fallback-close></div>
          <div class="cart-fallback-popup__dialog" role="dialog" aria-modal="true" aria-label="Cart updated">
            <h3 class="cart-fallback-popup__title" data-cart-fallback-title>Added to cart</h3>
            <p class="cart-fallback-popup__message" data-cart-fallback-message>Item has been added to your cart.</p>
            <div class="cart-fallback-popup__actions">
              <button type="button" class="cart-fallback-popup__button" data-cart-fallback-close>Continue shopping</button>
              <a class="cart-fallback-popup__button" data-cart-fallback-cart href="/cart">View cart</a>
              <a class="cart-fallback-popup__button cart-fallback-popup__button--primary" data-cart-fallback-checkout href="/checkout">Checkout</a>
            </div>
          </div>
        `;

        popup.querySelectorAll('[data-cart-fallback-close]').forEach((element) => {
          element.addEventListener('click', () => this.hideCartFallbackPopup());
        });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.hideCartFallbackPopup();
        });

        document.body.appendChild(popup);
        this.cartFallbackPopup = popup;
      }

      toggleSubmitButton(disable = true, text) {
        this.submitButtons.forEach((button) => {
          const buttonText = button.querySelector('span');
          const defaultText = button.dataset.defaultText || buttonText?.textContent || window.variantStrings.addToCart;

          if (disable) {
            button.setAttribute('disabled', 'disabled');
            if (text && buttonText) buttonText.textContent = text;
          } else {
            button.removeAttribute('disabled');
            if (buttonText) buttonText.textContent = defaultText;
          }
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      validateForm() {
        // Validate variant ID
        const variantId = this.variantIdInput?.value;
        if (!variantId || isNaN(variantId) || parseInt(variantId) <= 0) {
          return false;
        }

        // Validate quantity
        const quantityInput = this.form.querySelector('input[name="quantity"]');
        if (quantityInput) {
          const quantity = parseInt(quantityInput.value);
          const minQuantity = parseInt(quantityInput.min) || 1;
          const maxQuantity = parseInt(quantityInput.max) || 999;
          if (isNaN(quantity) || quantity < minQuantity || quantity > maxQuantity) {
            return false;
          }
        }

        // Validate other form fields if needed
        return true;
      }
    }
  );
}
